import * as React from "react";
import { Employee } from "@skbkontur/staff-common";
import {
    EmployeeListInitialState,
    EmployeeListOptions,
    StaffWidget,
    EmployeeListResult,
} from "@skbkontur/staff-selector-widget";
import { ThemeContext } from "@skbkontur/react-ui";

import { useAsyncEffect } from "../../../../../Common/UseAsyncEffect";
import { GFV } from "../../../../../Common/GFV";
import { ImportFromEmployeeServiceOptions } from "../../EmployeeServiceTypes";
import { getCandyHub } from "../../../Forms/Sentry/InitializeSentry";

export interface ImportEmployeesSidePageProps {
    active: boolean;
    staffServiceUrl: string;
    staffWidgetUrl: string;
    gfv: GFV;
    employerId?: string;
    initialState?: EmployeeListInitialState;
    options?: ImportFromEmployeeServiceOptions;
    onSubmit: (employees: Employee[]) => void;
    onClose: () => void;
}

export const ImportEmployeesSidePage = (props: ImportEmployeesSidePageProps) => {
    if (!(props.active && props.employerId)) {
        return <></>;
    }
    return <ImportPage employerId={props.employerId} {...props} />;
};

type ImportPageProps = Omit<ImportEmployeesSidePageProps, "active"> & {
    employerId: string;
};

const ImportPage = ({
    employerId,
    staffServiceUrl,
    staffWidgetUrl,
    onSubmit,
    options,
    initialState,
    gfv,
}: ImportPageProps) => {
    const candyCurrentTheme = React.useContext(ThemeContext);

    function importEmployees(result: EmployeeListResult) {
        onSubmit(result.employees);
    }

    const widget = React.useMemo(
        () =>
            new StaffWidget({
                productId: "keforms",
            }),
        []
    );

    useAsyncEffect(async () => {
        const { importWidgetModule } = await import(
            /* webpackChunkName: "staffSelectorNpmLoader"*/ "@skbkontur/staff-selector-npm-loader"
        );

        const initResponse = await importWidgetModule({
            widgetUrl: `${staffWidgetUrl}/assets/StaffSelectorWidget/latest/loader.js`,
            dependencies: {
                productId: "keforms",
                staffApiUrl: staffServiceUrl,
            },
        });
        const employeeListOptions: EmployeeListOptions = {
            employerId: employerId,
            additionalColumn: options?.additionalColumn ?? "inn",
            mode: options?.single ? "single" : "default",
            initialState: initialState,
            filters: ["employmentPeriod"],
            listType: options?.listType,
            // @ts-ignore пропс удалили в новой версии, а пакет завязан на react-ui 4.15.6
            theme: candyCurrentTheme,
            blockBackground: true,
            ignoreBackgroundClick: true,
            context: {
                gfv: gfv,
            },
            selected: options?.selected,
            card: {
                fields: {
                    sex: {
                        required: true,
                    },
                    residentStatus: {
                        required: true,
                    },
                    snils: {
                        required: true,
                    },
                    citizenship: {
                        required: true,
                    },
                    passport: {
                        required: true,
                    },
                    inn: {
                        required: false,
                    },
                    bankAccount: {
                        required: false,
                    },
                    mirCard: {
                        required: false,
                    },
                    organization: {
                        required: false,
                    },
                    email: {
                        required: false,
                    },
                    registrationAddress: {
                        required: false,
                    },
                    homeAddress: {
                        required: false,
                    },
                    phone: {
                        required: false,
                    },
                },
            },
        };

        if (initResponse.success) {
            const staffSelectorParams = {
                ...employeeListOptions,
                // @ts-ignore
                theme: candyCurrentTheme as StaffSelectorParams["theme"],
                onSubmit: importEmployees,
            };

            // @ts-ignore из-за разных версий staff-common в виджете и у нас
            const renderResponse = await initResponse.value.init(staffSelectorParams);

            if (!renderResponse.success) {
                const error = new Error(renderResponse.fault.message);
                error.name = `StaffWidget dynamic import error: ${renderResponse.fault.type}`;
                getCandyHub().captureException(error);
                // eslint-disable-next-line no-console
                console.error(error);
            }
        } else {
            const error = new Error(initResponse.fault.message);
            error.name = `StaffWidget dynamic import error: ${initResponse.fault.type}`;
            getCandyHub().captureException(error);
            // eslint-disable-next-line no-console
            console.error(error);

            const employees = await widget.getEmployeeList(employeeListOptions);
            importEmployees(employees);
        }
    }, []);

    return <></>;
};
